import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, IconButton, InputBase, makeStyles, Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import _ from 'lodash';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { CloseIcon, ScanBarcodeIcon } from '../../../../components/Icon';
import useIpad from '../../../../utils/useIpad';

const SCAN_RESULT_WARMUP_MS = 600;
// 电脑端扫码枪输入很快，间隔太久就当成普通键盘输入并清空缓存。
const SCANNER_KEY_INTERVAL = 80;
const SCANNER_SUBMIT_DELAY = 120;
const SCANNER_SUBMIT_KEYS = ['Enter', 'Tab'];
// 这些按键不是扫码内容，记录扫码缓存时需要忽略。
const SCANNER_KEY_BLACK_LIST = [
  'Backquote',
  'Shift',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Control',
  'Alt',
  'Meta',
  'Backspace',
  'Process',
];

const useStyles = makeStyles((theme) => ({
  search: {
    borderRadius: '0.625rem',
    backgroundColor: ({ highlight, draft }) => (highlight && draft ? highlight : theme.palette.font.white),
    width: ({ width }) => width,
    height: ({ height }) => height,
    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #e4e7eb',
    alignItems: 'center',
    padding: '0 0  0 1.25rem',
    gridGap: '0.714rem',
  },
  iconbtn: {
    padding: '0',
  },
  icon: {
    fontSize: '1.4rem',
  },
  input: ({ highlight, draft }) => ({
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    letterSpacing: '0',
    color: 'rgb(46, 68, 105)',
    fontWeight: '500',
    width: '100%',
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${highlight && draft ? highlight : theme.palette.font.white} inset`,
      WebkitTextFillColor: 'rgb(46, 68, 105)',
    },
  }),
}));

const useScanPortalStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    zIndex: 2000,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  closeIcon: {
    fontSize: '2rem',
    color: '#ffffff',
    position: 'absolute',
    top: '2rem',
    left: '2rem',
    zIndex: 1,
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const clearScanVideo = () => {
  const video = document.getElementById('scanQrcodeVideo');

  if (video) {
    video.pause();
    video.srcObject = null;
    video.removeAttribute('src');
  }
};

const useAeNoScanQrcode = (scanCallback) => {
  const [cameraPermission, setCameraPermission] = useState('');
  const codeReaderRef = useRef();
  const scanCallbackRef = useRef(scanCallback);
  const streamRef = useRef();
  const isActiveRef = useRef(false);
  const hasScannedRef = useRef(false);
  const acceptResultAfterRef = useRef(0);

  useEffect(() => {
    scanCallbackRef.current = scanCallback;
  }, [scanCallback]);

  const stopScan = useCallback(() => {
    isActiveRef.current = false;
    acceptResultAfterRef.current = 0;

    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = undefined;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = undefined;
    }

    clearScanVideo();
  }, []);

  const getStream = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: 'environment',
      },
    });
    streamRef.current = stream;
    return stream;
  }, []);

  const startScan = useCallback(async () => {
    stopScan();

    const codeReader = new BrowserMultiFormatReader();
    codeReaderRef.current = codeReader;
    isActiveRef.current = true;
    hasScannedRef.current = false;
    acceptResultAfterRef.current = Date.now() + SCAN_RESULT_WARMUP_MS;

    try {
      const stream = await getStream();

      if (!isActiveRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        if (streamRef.current === stream) {
          streamRef.current = undefined;
        }
        return;
      }

      await codeReader.decodeFromStream(stream, 'scanQrcodeVideo', (result, err) => {
        if (result) {
          // iPad 浏览器刚打开摄像头时可能短暂吐出上一轮旧帧，前几百毫秒不接收结果。
          if (!isActiveRef.current || hasScannedRef.current || Date.now() < acceptResultAfterRef.current) {
            return;
          }

          hasScannedRef.current = true;
          isActiveRef.current = false;
          setCameraPermission('finish');
          scanCallbackRef.current(result.text);
          codeReader.reset();
        }

        if (err && !(err instanceof NotFoundException)) {
          console.error(err);
        }
      });

      if (isActiveRef.current && !hasScannedRef.current) {
        setCameraPermission('success');
      }
    } catch (error) {
      console.log(error);
      setCameraPermission('fail');
    }
  }, [getStream, stopScan]);

  useEffect(() => {
    setCameraPermission('');
    startScan();

    return () => stopScan();
  }, [startScan, stopScan]);

  return [cameraPermission];
};

// 单独拆出摄像头组件，方便旋转后重新挂载并重新打开摄像头。
const AeNoScanCamera = ({ classes, onClose, scanCallback }) => {
  const [cameraPermission] = useAeNoScanQrcode(scanCallback);

  useEffect(() => {
    // 摄像头失败或扫码完成后关闭全屏扫码层
    if (cameraPermission === 'fail' || cameraPermission === 'finish') {
      onClose();
    }
  }, [cameraPermission, onClose]);

  return (
    <>
      <video id="scanQrcodeVideo" className={classes.video} autoPlay playsInline muted />
      {cameraPermission === 'success' && <CloseIcon id="close" onClick={onClose} className={classes.closeIcon} />}
    </>
  );
};

const AeNoScanVideo = ({ onClose, scanCallback, isIpad }) => {
  // cameraKey 变化会重新挂载 AeNoScanCamera，从而重新执行 getUserMedia。
  const [cameraKey, setCameraKey] = useState(0);
  const restartTimerRef = useRef();
  const classes = useScanPortalStyles();

  useEffect(() => {
    // iPad 旋转后等视口稳定，再重开摄像头，避免沿用旧方向的 camera stream。
    const handleOrientationChange = () => {
      if (!isIpad) {
        return;
      }

      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = window.setTimeout(() => {
        setCameraKey((prevKey) => prevKey + 1);
      }, 350);
    };

    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.clearTimeout(restartTimerRef.current);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isIpad]);

  return (
    <div className={classes.root}>
      <AeNoScanCamera key={cameraKey} classes={classes} onClose={onClose} scanCallback={scanCallback} />
    </div>
  );
};

const AeNoScanPortal = ({ open, onClose, scanCallback, isIpad }) => {
  const [portalElement] = useState(() => document.createElement('div'));

  useEffect(() => {
    // 把扫码画面挂到 body，避免被当前页面布局遮住
    if (!open) {
      return undefined;
    }

    document.body.appendChild(portalElement);

    return () => {
      if (portalElement.parentNode) {
        portalElement.parentNode.removeChild(portalElement);
      }
    };
  }, [open, portalElement]);

  if (!open) {
    return null;
  }

  return createPortal(<AeNoScanVideo onClose={onClose} scanCallback={scanCallback} isIpad={isIpad} />, portalElement);
};

const AeNoScanInput = ({
  value,
  onChange,
  onBarcodeScan,
  disabled = false,
  width = '18rem',
  height = '2.5625rem',
  placeholder = 'AE No.',
  inputId = 'e-admission-aeno',
  scanButtonId = 'e-admission-scan-barcode-icon',
  debounceTime = 1000,
  highlight,
}) => {
  const isIpad = useIpad();
  const [scanOpen, setScanOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [scanErrorMessage, setScanErrorMessage] = useState('');
  const scanBufferRef = useRef('');
  const lastScanKeyTimeRef = useRef(0);
  const scanSubmitTimerRef = useRef();
  // draft 用来即时显示输入内容，真正筛选值用 debounce 后再提交
  const [draft, setDraft] = useState(value ?? '');
  const classes = useStyles({ width, height, highlight, draft });
  const submitValue = useMemo(
    () =>
      _.debounce((nextValue) => {
        onChange(nextValue);
      }, debounceTime),
    [debounceTime, onChange]
  );

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    return () => {
      submitValue.cancel();
    };
  }, [submitValue]);

  const handleScanResult = useCallback(
    (scanValue) => {
      // 统一处理 iPad 摄像头和扫码枪扫到的 AE No.
      if (!scanValue || disabled) {
        return;
      }

      onBarcodeScan?.(scanValue);
      submitValue.cancel();
      setDraft(scanValue);
      onChange(scanValue);
    },
    [disabled, onBarcodeScan, onChange, submitValue]
  );

  // 清空电脑端扫码枪的本地输入缓存。
  const clearScannerBuffer = useCallback(() => {
    window.clearTimeout(scanSubmitTimerRef.current);
    scanBufferRef.current = '';
    lastScanKeyTimeRef.current = 0;
  }, []);

  // 提交当前缓存里的扫码枪结果。
  const submitScannerBuffer = useCallback(() => {
    const scanValue = scanBufferRef.current;
    clearScannerBuffer();

    if (scanValue.length > 1) {
      handleScanResult(scanValue);
    }
  }, [clearScannerBuffer, handleScanResult]);

  useEffect(() => {
    // 电脑端扫码枪只在AE No输入框focus时接收，iPad摄像头扫码不走这里。
    if (disabled || isIpad || scanOpen || !isInputFocused) {
      clearScannerBuffer();
      return undefined;
    }

    // 收集扫码枪模拟的键盘输入，扫完后提交AE No。
    const handleScannerKeyDown = (event) => {
      const now = Date.now();

      if (SCANNER_SUBMIT_KEYS.includes(event.key)) {
        if (scanBufferRef.current.length > 1) {
          event.preventDefault();
          submitScannerBuffer();
        } else {
          clearScannerBuffer();
        }
        return;
      }

      // 只在输入框focus期间记录扫码枪输入，避免拼接其它地方的键盘内容。
      if (!SCANNER_KEY_BLACK_LIST.includes(event.key)) {
        if (lastScanKeyTimeRef.current && now - lastScanKeyTimeRef.current > SCANNER_KEY_INTERVAL) {
          clearScannerBuffer();
        }

        lastScanKeyTimeRef.current = now;
        scanBufferRef.current += event.key;
        window.clearTimeout(scanSubmitTimerRef.current);
        // 没有结束符的扫码枪，用短暂停顿当作扫码完成。
        scanSubmitTimerRef.current = window.setTimeout(submitScannerBuffer, SCANNER_SUBMIT_DELAY);
      }
    };

    window.addEventListener('keydown', handleScannerKeyDown);

    return () => {
      clearScannerBuffer();
      window.removeEventListener('keydown', handleScannerKeyDown);
    };
  }, [clearScannerBuffer, disabled, isInputFocused, isIpad, scanOpen, submitScannerBuffer]);

  const handleInputFocus = () => {
    // 每次重新focus都清空本地扫码缓存，确保只接收之后的新扫码。
    clearScannerBuffer();
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    clearScannerBuffer();
    setIsInputFocused(false);
  };

  const handleInputChange = (event) => {
    // 手动输入时先更新输入框，再延迟触发外部筛选
    if (disabled) {
      return;
    }

    const nextValue = event.target.value;
    setDraft(nextValue);
    submitValue(nextValue);
  };

  const handleScanBarcode = useCallback(() => {
    // iPad 需要安全上下文，浏览器才允许打开摄像头 API
    if (!isIpad || disabled) {
      return;
    }

    if (!window.isSecureContext) {
      setScanErrorMessage('Camera scanning on iPad requires HTTPS.');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setScanErrorMessage('Camera scanning is not supported by this browser.');
      return;
    }

    setScanOpen(true);
  }, [disabled, isIpad]);

  return (
    <>
      <AeNoScanPortal
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        scanCallback={handleScanResult}
        isIpad={isIpad}
      />
      <Snackbar
        open={Boolean(scanErrorMessage)}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setScanErrorMessage('')}
      >
        <MuiAlert elevation={6} variant="filled" severity="warning" onClose={() => setScanErrorMessage('')}>
          {scanErrorMessage}
        </MuiAlert>
      </Snackbar>
      <Box className={classes.search}>
        {isIpad && (
          <IconButton id={scanButtonId} className={classes.iconbtn} onClick={handleScanBarcode} disabled={disabled}>
            <ScanBarcodeIcon className={classes.icon} />
          </IconButton>
        )}
        <InputBase
          id={inputId}
          className={classes.input}
          value={draft}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          placeholder={placeholder}
        />
      </Box>
    </>
  );
};

export default AeNoScanInput;
